import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { X, Trash2 } from "lucide-react";

const STATUSES = ["new", "contacted", "qualified", "closed"];

export default function AdminLeads() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);

  const load = () => api.get("/admin/leads").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    await api.put(`/admin/leads/${id}/status`, { status });
    toast.success("Status updated");
    load();
    if (open?.id === id) setOpen({ ...open, status });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this lead?")) return;
    await api.delete(`/admin/leads/${id}`);
    toast.success("Lead deleted");
    setOpen(null);
    load();
  };

  return (
    <div data-testid="admin-leads-page">
      <div className="overline mb-2">Leads / RFQ</div>
      <h1 className="font-heading font-extrabold text-3xl tracking-tight mb-6">Manage Leads</h1>

      <div className="bg-white border border-msr-line overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-msr-mist text-left text-[11px] uppercase tracking-wider text-msr-slate">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Inquiry Type</th>
              <th className="px-4 py-3">Company</th>
              <th className="px-4 py-3">Solution</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={7} className="px-4 py-10 text-center text-msr-slate" data-testid="admin-leads-empty">No leads yet.</td></tr>
            )}
            {items.map((l) => (
              <tr key={l.id} className="border-t border-msr-line" data-testid={`admin-lead-row-${l.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold">{l.name}</div>
                  <div className="text-xs text-msr-slate">{l.email}</div>
                </td>
                <td className="px-4 py-3 uppercase text-xs text-msr-slate">{l.inquiry_type}</td>
                <td className="px-4 py-3 text-msr-slate">{l.company || "—"}</td>
                <td className="px-4 py-3 text-msr-slate">{l.solution_slug || "—"}</td>
                <td className="px-4 py-3">
                  <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)} className="border border-msr-line bg-white text-xs px-2 py-1.5" data-testid={`admin-lead-status-${l.id}`}>
                    {STATUSES.map((s) => (<option key={s} value={s}>{s}</option>))}
                  </select>
                </td>
                <td className="px-4 py-3 text-xs text-msr-slate">{formatDate(l.created_at)}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setOpen(l)} className="text-msr-blue text-xs mr-3" data-testid={`admin-lead-view-${l.id}`}>View</button>
                  <button onClick={() => remove(l.id)} className="text-red-500 text-xs inline-flex items-center gap-1" data-testid={`admin-lead-delete-${l.id}`}><Trash2 size={11} /> Delete</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-40 bg-black/60 grid place-items-center p-4" data-testid="admin-lead-detail">
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto border-t-4 border-msr-green">
            <div className="border-b border-msr-line px-6 py-4 flex justify-between">
              <div>
                <div className="overline">Lead</div>
                <div className="font-heading font-bold text-lg">{open.name}</div>
              </div>
              <button onClick={() => setOpen(null)} className="p-2"><X size={20} /></button>
            </div>
            <div className="p-6 grid sm:grid-cols-2 gap-4 text-sm">
              <Info label="Inquiry type" value={open.inquiry_type} />
              <Info label="Solution" value={open.solution_slug || "—"} />
              <Info label="Email" value={open.email} />
              <Info label="Phone" value={open.phone || "—"} />
              <Info label="Company" value={open.company || "—"} />
              <Info label="Position" value={open.position || "—"} />
              <Info label="Industry" value={open.industry || "—"} />
              <Info label="Budget" value={open.budget_range || "—"} />
              <Info label="Timeline" value={open.timeline || "—"} />
              <Info label="Status" value={open.status} />
              <div className="sm:col-span-2"><Info label="Requirements" value={open.requirements || "—"} /></div>
              <div className="sm:col-span-2"><Info label="Notes" value={open.notes || "—"} /></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="overline mb-1">{label}</div>
      <div className="text-sm font-semibold whitespace-pre-line text-msr-ink">{value}</div>
    </div>
  );
}
